<template>
  <Dialog :common-dialog="dialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between w-100 px-2 py-1">
        <div class="d-flex align-center">
          <v-avatar size="38" color="cyan lighten-5" class="mr-3">
            <v-icon color="cyan darken-2">mdi-account-circle-outline</v-icon>
          </v-avatar>
          <div class="dialog-title color-custom-blue font-weight-700">
            Select a Contact Person
          </div>
        </div>
        <v-btn
          icon
          small
          class="custom-grey-border bg-slate-50"
          :disabled="loading"
          v-on:click="$emit('close', true)"
        >
          <v-icon color="slate-600" small>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>
    <template v-slot:body>
      <div class="px-4 w-100">
        <!-- SEARCH & ACTION TOOLBAR -->
        <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-2 mb-3">
          <label
            for="contact-person-search"
            class="font-size-13 font-weight-600 mb-1 d-block text-slate-700"
          >
            Which contact person would you like to select this for?
          </label>
          <v-row dense align="center">
            <v-col cols="12" sm="8" md="8">
              <v-text-field
                id="contact-person-search"
                v-model="search"
                placeholder="Search Contact Person..."
                dense
                filled
                solo
                flat
                hide-details
                color="cyan"
                prepend-inner-icon="mdi-magnify"
                :loading="loading"
                :disabled="loading"
                class="custom-erp-input bg-white rounded-lg"
                @keydown.enter="filterPersons()"
              />
            </v-col>
            <v-col cols="12" sm="4" md="4" class="d-flex align-center">
              <v-btn
                v-on:click="filterPersons()"
                :loading="loading"
                :disabled="loading"
                depressed
                color="cyan darken-2 white--text"
                class="custom-bold-button w-100"
                height="36"
              >
                <v-icon small left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- PERSON LIST AREA -->
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll pr-1"
          style="max-height: calc(100vh - 340px); position: relative"
        >
          <div v-if="list.length">
            <v-card
              v-for="(row, index) in list"
              :key="`contact-person-list-key-${index}`"
              flat
              outlined
              class="rounded-lg px-3 py-2 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
              v-on:click="selectPerson(row)"
            >
              <v-row dense align="center">
                <!-- Left: Person Details -->
                <v-col cols="12" md="8" class="py-1">
                  <div class="d-flex align-center flex-wrap gap-2 mb-1">
                    <Barcode :barcode="row.barcode" />
                    <v-chip
                      v-if="row.designation"
                      x-small
                      color="cyan lighten-5"
                      class="cyan--text text-darken-2 font-weight-600"
                    >
                      {{ row.designation }}
                    </v-chip>
                  </div>

                  <div
                    class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                  >
                    <v-icon small left color="slate-600"
                      >mdi-account-outline</v-icon
                    >
                    <template v-if="row.name">
                      {{ row.name }}
                    </template>
                    <em v-else class="text-muted font-italic font-weight-400"
                      >no name available</em
                    >
                  </div>

                  <div
                    class="d-flex align-center flex-wrap font-size-12 text-slate-700 gap-3"
                  >
                    <span class="d-flex align-center mr-3">
                      <v-icon small left color="slate-500"
                        >mdi-email-outline</v-icon
                      >
                      <template v-if="row.email">
                        {{ row.email }}
                      </template>
                      <em v-else class="text-muted">no email</em>
                    </span>

                    <span class="d-flex align-center">
                      <v-icon small left color="slate-500"
                        >mdi-phone-outline</v-icon
                      >
                      <template v-if="row.phone">
                        {{ row.phone }}
                      </template>
                      <em v-else class="text-muted">no phone number</em>
                    </span>
                  </div>
                </v-col>

                <!-- Right: Select CTA -->
                <v-col
                  cols="12"
                  md="4"
                  class="text-md-right d-flex align-center justify-md-end py-1"
                >
                  <v-btn
                    small
                    depressed
                    color="cyan lighten-5"
                    class="custom-bold-button px-3 cyan--text text-darken-2"
                  >
                    Select Person
                    <v-icon small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <!-- EMPTY STATE -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
          >
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon color="slate-400" large>mdi-account-off-outline</v-icon>
            </v-avatar>
            <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
              No Contact Persons Found
            </div>
            <div class="caption-1 text-muted max-w-300">
              There are no matching contact person records in the database.
            </div>
          </div>
        </perfect-scrollbar>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { GET } from "@/core/services/store/request.module";
import ObjectPath from "object-path";

export default {
  name: "person-select-opportunity",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    parentId: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      default: "lead",
    },
  },
  data() {
    return {
      loading: false,
      search: null,
      list: [],
      fullList: [],
    };
  },
  watch: {
    dialog(param) {
      this.search = null;
      if (param) {
        this.getPersons();
      }
    },
  },
  components: {
    Dialog,
    Barcode,
  },
  methods: {
    selectPerson(row) {
      this.$emit("select", row);
      this.$emit("close", true);
      this.loading = false;
      this.search = null;
      this.list = [];
    },
    getPersons() {
      this.loading = true;
      this.list = [];
      const url = `contact-person-list/${this.parentId}?type=${this.type}`;
      this.$store
        .dispatch(GET, { url })
        .then((response) => {
          const list = ObjectPath.get(response, "data", []).map((person) => ({
            ...person,
            name: person.name || person.full_name || "",
            email: person.email || person.primary_email || "",
            phone: person.phone || person.primary_phone || "",
            designation: person.designation || person.position || "",
          }));
          this.fullList = list;
          this.filterPersons();
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    filterPersons() {
      if (!this.search || this.search.trim() === "") {
        this.list = this.fullList;
      } else {
        const searchLower = this.search.toLowerCase();
        this.list = this.fullList.filter(
          (person) =>
            (person.name && person.name.toLowerCase().includes(searchLower)) ||
            (person.email &&
              person.email.toLowerCase().includes(searchLower)) ||
            (person.designation &&
              person.designation.toLowerCase().includes(searchLower))
        );
      }
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 40);
    },
  },
};
</script>
